/**
 * Client-side access-control helpers + route guard (Phase 8).
 *
 * These gate what the UI *shows*. They are a defense-in-depth UX layer only —
 * the server re-checks every request (P8 DoD: "No security decision depends on
 * the frontend"). Guards are intentionally lenient when the permission set
 * hasn't loaded (bootstrap failed / pre-P8 token): they fall back to the legacy
 * role check so an admin is never locked out of a screen the server would allow.
 */

import React from 'react';
import { Route } from 'react-router-dom';
import { Box, Typography, Paper } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Colors from './ui/Colors';

const rolesOf = (loginUser) =>
  (loginUser && Array.isArray(loginUser.roles)) ? loginUser.roles : [];

const permsOf = (loginUser) =>
  (loginUser && Array.isArray(loginUser.permissions)) ? loginUser.permissions : [];

/** Do we actually have a resolved permission set to reason about? */
export const permissionsLoaded = (loginUser) => permsOf(loginUser).length > 0;

export const hasRole = (loginUser, authority) =>
  rolesOf(loginUser).some((r) => r && r.authority === authority);

export const isAdmin = (loginUser) => hasRole(loginUser, 'ADMIN');

/** ROOT: prefer the server-provided platform scope, then perms, then role. */
export const isRoot = (loginUser) => {
  if (loginUser && loginUser.platformScope === true) return true;
  if (permsOf(loginUser).some((p) => typeof p === 'string' && p.indexOf('PLATFORM_') === 0)) return true;
  return hasRole(loginUser, 'ROOT');
};

export const hasPermission = (loginUser, permission) =>
  permsOf(loginUser).indexOf(permission) !== -1;

export const hasAnyPermission = (loginUser, permissions) =>
  Array.isArray(permissions) && permissions.some((p) => hasPermission(loginUser, p));

/**
 * Authorization decision for a screen.
 * @param anyOf        permission names — authorized if the user has ANY of them
 * @param roleFallback legacy role checked when no permission set is loaded
 * @param requireRoot  when true, ROOT is additionally required
 */
export const isAuthorized = (loginUser, { anyOf = [], roleFallback = null, requireRoot = false } = {}) => {
  if (requireRoot && !isRoot(loginUser)) return false;

  if (anyOf.length > 0) {
    if (hasAnyPermission(loginUser, anyOf)) return true;
    // Perms loaded but none matched → deny (unless ROOT, who transcends tenant perms).
    if (permissionsLoaded(loginUser)) return isRoot(loginUser);
  }
  // No perms loaded (degraded) or no anyOf specified → fall back to role.
  if (roleFallback) return hasRole(loginUser, roleFallback);
  return anyOf.length === 0; // no constraint at all → allow
};

export function AccessDenied({ message }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8, px: 2 }}>
      <Paper variant="outlined" sx={{ p: 4, maxWidth: 480, textAlign: 'center', borderRadius: 3 }}>
        <LockOutlinedIcon sx={{ fontSize: 56, color: Colors.primary, mb: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Access restricted</Typography>
        <Typography variant="body2" color="text.secondary">
          {message || "You don't have permission to view this page. If you believe this is an error, contact your administrator."}
        </Typography>
      </Paper>
    </Box>
  );
}

/**
 * A React Router v5 route that only renders its component when the current
 * user is authorized; otherwise it renders <AccessDenied/>. Mirrors the
 * existing render pattern: pass `component`, `mainProps`, and the gate config.
 */
export function ProtectedRoute({ component: Component, mainProps, anyOf, roleFallback, requireRoot, deniedMessage, ...routeProps }) {
  const loginUser = mainProps && mainProps.loginUser;
  const allowed = isAuthorized(loginUser, { anyOf, roleFallback, requireRoot });
  return (
    <Route
      {...routeProps}
      render={(props) =>
        allowed
          ? React.createElement(Component, { ...props, ...mainProps })
          : <AccessDenied message={deniedMessage} />
      }
    />
  );
}
